import { Checkbox, Form, Input, SubmitButton, SuggestionsInput, useZodForm } from '@/components/Forms'
import UserIcon from '../assets/user.svg'
import { confirmDetailsSchema } from '../schema'
import { useSendConfirmDetails } from '../api/useSendConfirmDetails'
import { useGetUserInfo } from '@/modules/Auth'
import styles from './ConfirmDetailsModal.module.scss'
import { useGetConfirmDetails } from '../api/useGetConfirmDetails'
import { CONFIRM_DETAILS_FORM_FIELDS } from '../constants'
import { ShiftTime } from './components/ShiftTime'
import { useEffect, useRef } from 'react'
import { z } from 'zod'
import { useAccount, useMsal } from '@azure/msal-react'
import { getPersistedUserDetails, persistUserDetails, UserDetailsFormData } from './rememberDetails'
import { suggestDuty } from './suggestDuty'

export const ConfirmDetailsForm = () => {
    const { accounts } = useMsal()
    const account = useAccount(accounts[0] || {})
    const { data: confirmDetailsData } = useGetConfirmDetails()
    const getFormDefaultValues = (persistedData?: UserDetailsFormData) => ({
        [CONFIRM_DETAILS_FORM_FIELDS.busHubCode]: persistedData?.busHubCode || '',
        [CONFIRM_DETAILS_FORM_FIELDS.dutyCode]: persistedData?.dutyCode || '',
        [CONFIRM_DETAILS_FORM_FIELDS.badgeNumber]: persistedData?.badgeNumber || '',
        [CONFIRM_DETAILS_FORM_FIELDS.noBadge]: persistedData?.noBadge || false,
        [CONFIRM_DETAILS_FORM_FIELDS.noDuty]: persistedData?.noDuty || false,
    })

    const form = useZodForm({
        schema: confirmDetailsSchema,
        defaultValues: getFormDefaultValues(),
    })
    const { handleSendConfirmDetails, isPending } = useSendConfirmDetails()
    const { data: userInfo } = useGetUserInfo()

    if (!confirmDetailsData) throw new Error('No data received from the server')

    const selectedDutyCode = form.watch(CONFIRM_DETAILS_FORM_FIELDS.dutyCode)
    const selectedHubTitle = form.watch(CONFIRM_DETAILS_FORM_FIELDS.busHubCode)
    const selectedHubIBusCode = confirmDetailsData.hubs.find((hub) => hub.hubTitle === selectedHubTitle)?.iBusCode
    const filteredDuties = confirmDetailsData.duties.filter((duty) => duty.iBusHubCode === selectedHubIBusCode)

    const hasInitializedFromPersistedData = useRef(false)
    const previousHubTitle = useRef<string>()

    useEffect(() => {
        const userId = account?.localAccountId
        if (!hasInitializedFromPersistedData.current && confirmDetailsData && userId) {
            hasInitializedFromPersistedData.current = true
            const persistedData = getPersistedUserDetails(account?.localAccountId || '')

            if (persistedData) {
                form.reset(getFormDefaultValues(persistedData))
                const persistedDuty = persistedData?.dutyCode
                const persistedHub = persistedData.busHubCode

                previousHubTitle.current = persistedHub

                if (persistedDuty && persistedHub) {
                    const hubIBusCode = confirmDetailsData.hubs.find((hub) => hub.hubTitle === persistedHub)?.iBusCode

                    if (hubIBusCode) {
                        const hubDuties = confirmDetailsData.duties.filter((duty) => duty.iBusHubCode === hubIBusCode)
                        const suggestedDuty = suggestDuty(
                            persistedDuty,
                            hubDuties.map((duty) => duty.dutyCode)
                        )
                        if (suggestedDuty && suggestedDuty !== persistedDuty) {
                            form.setValue(CONFIRM_DETAILS_FORM_FIELDS.dutyCode, suggestedDuty, {
                                shouldValidate: true,
                            })
                        }
                    }
                }
            }
        }
    }, [confirmDetailsData, account, filteredDuties, form])

    const handleHubChange = () => {
        if (selectedHubTitle) {
            form.setValue(CONFIRM_DETAILS_FORM_FIELDS.dutyCode, undefined)
        }
    }

    const handleSubmit = (data: z.infer<typeof confirmDetailsSchema>) => {
        if (!confirmDetailsData.hubs.map((hub) => hub.hubTitle).includes(selectedHubTitle)) {
            form.setError(CONFIRM_DETAILS_FORM_FIELDS.busHubCode, {
                message: 'Hub not found',
            })
            return
        }
        if (selectedDutyCode && !filteredDuties.map((duty) => duty.dutyCode).includes(selectedDutyCode)) {
            form.setError(CONFIRM_DETAILS_FORM_FIELDS.dutyCode, {
                message: 'Duty not found',
            })
            return
        }
        handleSendConfirmDetails(data)

        const userId = account?.localAccountId
        if (userId) {
            persistUserDetails(userId, data)
        }
    }

    return (
        <Form form={form} onSubmit={handleSubmit}>
            <div className={styles.formContainer}>
                <h1 className={styles.title}>Confirm details</h1>
                <div className={styles.userContainer}>
                    <UserIcon />
                    <span className={styles.name}>{userInfo?.userDetails.name}</span>
                </div>

                <div className={styles.fieldsContainer}>
                    <div className={styles.fieldWrapper}>
                        <Input
                            {...form.register(CONFIRM_DETAILS_FORM_FIELDS.badgeNumber)}
                            allowClear
                            id="badgeNumber"
                            type="text"
                            onChange={(e) => {
                                // Only allow digits
                                const value = e.target.value.replace(/[^0-9]/g, '')
                                e.target.value = value
                                return value
                            }}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            label="Badge number"
                            placeholder="Badge number"
                            disabled={form.watch(CONFIRM_DETAILS_FORM_FIELDS.noBadge)}
                            fullWidth
                            iconClassName={styles.badgeInput}
                        />
                        <Checkbox name={CONFIRM_DETAILS_FORM_FIELDS.noBadge} label="I don’t have a badge number yet" />
                    </div>

                    <SuggestionsInput
                        options={confirmDetailsData.hubs.map((hub) => ({
                            label: hub.hubTitle,
                            value: hub.hubTitle,
                            id: hub.hubTitle,
                        }))}
                        setSelectedValue={handleHubChange}
                        placeholder="Hub"
                        label="Hub"
                        {...form.register(CONFIRM_DETAILS_FORM_FIELDS.busHubCode)}
                    />

                    <div className={styles.fieldWrapper}>
                        <SuggestionsInput
                            {...form.register(CONFIRM_DETAILS_FORM_FIELDS.dutyCode)}
                            options={filteredDuties.map((duty) => ({
                                label: duty.dutyCode,
                                value: duty.dutyCode,
                                id: duty.dutyCode,
                            }))}
                            setSelectedValue={(value) => form.setValue(CONFIRM_DETAILS_FORM_FIELDS.dutyCode, value)}
                            placeholder="Duty"
                            label="Duty"
                            disabled={form.watch(CONFIRM_DETAILS_FORM_FIELDS.noDuty)}
                        />

                        <ShiftTime form={form} />
                    </div>
                </div>

                <div className={styles.submitButtonContainer}>
                    <SubmitButton
                        className={styles.submitButton}
                        disabled={!form.formState.isValid || isPending}
                        rounded
                    >
                        Start shift
                    </SubmitButton>

                    {form.formState.isValid && (
                        <span className={styles.submitButtonText}>
                            Log entries will be created with shift information
                        </span>
                    )}
                </div>
            </div>
        </Form>
    )
}
